import React, { useEffect, useState } from 'react';
import { Avatar, Chip, Pagination, Button, CircularProgress } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { StarOutlineRounded } from '@mui/icons-material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../../../components/common/search/SearchBar';
import FilterSelect from '../../../components/common/filter/FilterSelect';
import useTeachers from '../../../hooks/useTeachers';
import AppSnackbar from '../../../components/common/popups/AppSnackbar';
import ConfirmDialog from '../../../components/common/popups/ConfirmDialog';
import { deleteProfile } from '../../../services/teacher.service';
import ReviewsRecsDrawer from '../../../components/admin/ReviewsRecsDrawer';
import TeacherFeeModal from '../../../components/admin/modals/TeacherFeeModal';
import PrimaryButton from '../../../components/common/buttons/PrimaryButton';
import { fetchEduLevelSubjects } from '../../../services/masterData.service';

export default function Teachers() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [appliedSubjectFilter, setAppliedSubjectFilter] = useState('');
  const [appliedStatusFilter, setAppliedStatusFilter] = useState('');
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [drawerTeacherId, setDrawerTeacherId] = useState(null);
  const [feeModalTeacher, setFeeModalTeacher] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    teacherId: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const { teachers, meta, loading, error, refetch } = useTeachers(currentPage, 6, {
    search: appliedSearchQuery,
    subject: appliedSubjectFilter,
    status: appliedStatusFilter,
  });

  const drawerTeacher = drawerTeacherId
    ? (teachers.find((t) => t.id === drawerTeacherId) ?? null)
    : null;

  useEffect(() => {
    let isMounted = true;

    const loadSubjects = async () => {
      try {
        const res = await fetchEduLevelSubjects();
        const options = (res.data ?? []).map((subject) => ({
          value: subject.id,
          label: subject.name,
        }));

        if (isMounted) setSubjectOptions(options);
      } catch (err) {
        if (isMounted) setSubjectOptions([]);
      }
    };

    loadSubjects();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasActiveFilters = appliedSearchQuery || appliedSubjectFilter || appliedStatusFilter;
  const hasFilterChanges =
    searchQuery.trim() !== appliedSearchQuery ||
    subjectFilter !== appliedSubjectFilter ||
    statusFilter !== appliedStatusFilter;

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const applyFilters = () => {
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSubjectFilter(subjectFilter);
    setAppliedStatusFilter(statusFilter);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSubjectFilter('');
    setStatusFilter('');
    setAppliedSearchQuery('');
    setAppliedSubjectFilter('');
    setAppliedStatusFilter('');
    setCurrentPage(1);
  };

  const copyProfileUrl = (teacherId) => {
    const url = `${window.location.origin}/teacher/${teacherId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(teacherId);
      setSnackbar({
        open: true,
        message: 'Profile link copied!',
        severity: 'success',
      });
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleEdit = (teacherId) => {
    navigate(`/admin/edit-teacher/${teacherId}`);
  };

  const handleDeleteClick = (id) => {
    setConfirmDialog({ open: true, teacherId: id });
  };

  const handleConfigFee = (teacher) => {
    setFeeModalTeacher(teacher);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await deleteProfile(confirmDialog.teacherId);
      setConfirmDialog({ open: false, teacherId: null });
      refetch();
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Something went wrong. Please try again.',
        severity: 'error',
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <CircularProgress sx={{ color: '#F6911D' }} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-red-400 text-lg">{error}</p>
        <Button
          onClick={refetch}
          sx={{
            color: '#F6911D',
            textTransform: 'none',
            border: '1px solid #F6911D',
            '&:hover': { background: 'rgba(246, 145, 29, 0.1)' },
          }}
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-light">Teachers</h1>
          <p className="text-gray-400 mt-1">Manage and view all teachers</p>
        </div>
        <PrimaryButton onClick={() => navigate('/admin/create-teacher')}>
          <PersonAddAltOutlinedIcon className="me-2" />
          Add Teacher
        </PrimaryButton>
      </div>

      {/* Search and Filters */}
      <div className="bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name or email..."
            />
          </div>
          <FilterSelect
            label="Subject"
            value={subjectFilter}
            onChange={setSubjectFilter}
            options={subjectOptions}
          />
          {/*<FilterSelect*/}
          {/*  label="Status"*/}
          {/*  value={statusFilter}*/}
          {/*  onChange={setStatusFilter}*/}
          {/*  options={statusOptions}*/}
          {/*/>*/}
          <Button
            variant="contained"
            disabled={!hasFilterChanges}
            onClick={applyFilters}
            sx={{
              height: '56px',
              background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { opacity: 0.9 },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.35)',
              },
            }}
          >
            Filter
          </Button>
        </div>

        {hasActiveFilters && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {teachers.length} of {meta?.total ?? teachers.length} results
            </p>
            <Button
              size="small"
              onClick={clearFilters}
              sx={{
                color: '#F6911D',
                textTransform: 'none',
                '&:hover': { background: 'rgba(246, 145, 29, 0.1)' },
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Teacher Cards Grid */}
      {teachers.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {teachers.map((teacher) => {
              const fullName = `${teacher.firstName} ${teacher.lastName}`;
              const isActive = teacher.status;

              return (
                <div
                  key={teacher.id}
                  className="bg-gradient-glass backdrop-blur-sm p-6 rounded-lg shadow-lg border border-white/10 hover:scale-105 transition-all duration-300"
                >
                  {/* Header with Avatar and Status */}
                  <div className="flex flex-col-reverse sm:flex-row items-start sm:justify-between mb-4">
                    <div className="flex flex-col sm:flex-row text-center sm:text-left items-center gap-3 w-full">
                      <Avatar
                        src={teacher.avatar ?? undefined}
                        sx={{
                          width: { xs: 100, sm: 56 },
                          height: { xs: 100, sm: 56 },
                          background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%)',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {teacher.firstName.charAt(0)}
                      </Avatar>
                      <div>
                        <h3 className="text-lg font-semibold text-light">{fullName}</h3>
                        <p className="text-sm text-gray-400">
                          {teacher.subjects?.map((s) => s.name).join(', ') || 'No subjects'}
                        </p>
                      </div>
                    </div>
                    <Chip
                      label={isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        marginLeft: 'auto',
                        backgroundColor: isActive
                          ? 'rgba(34, 197, 94, 0.2)'
                          : 'rgba(156, 163, 175, 0.2)',
                        color: isActive ? '#22c55e' : '#9ca3af',
                        fontWeight: 600,
                        border: `1px solid ${
                          isActive ? 'rgba(34, 197, 94, 0.3)' : 'rgba(156, 163, 175, 0.3)'
                        }`,
                      }}
                    />
                  </div>

                  {/* Teacher Info */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center gap-2 text-gray-300">
                      <EmailOutlinedIcon sx={{ fontSize: 18, color: '#F6911D' }} />
                      <span className="text-sm truncate">{teacher.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <PhoneOutlinedIcon sx={{ fontSize: 18, color: '#F6911D' }} />
                      <span className="text-sm truncate">{teacher.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <PeopleOutlinedIcon sx={{ fontSize: 18, color: '#F6BC1D' }} />
                      <span className="text-sm">
                        {teacher.cities?.map((c) => c.name).join(', ') || 'No cities'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-4 border-t border-white/10">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleEdit(teacher.profileId)}
                        className="flex-1 px-4 py-2 bg-gradient-glass hover:bg-yellow-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-yellow-500/20 hover:border-yellow-500/50"
                      >
                        <EditNoteIcon sx={{ fontSize: 18 }} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(teacher.profileId)}
                        className="flex-1 px-4 py-2 bg-gradient-glass hover:bg-red-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-red-500/20 hover:border-red-500/50"
                      >
                        <DeleteForeverOutlinedIcon sx={{ fontSize: 18 }} />
                        Delete
                      </button>
                      <button
                        onClick={() => handleConfigFee(teacher)}
                        className="flex-1 px-4 py-2 bg-gradient-glass hover:bg-emerald-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-emerald-500/20 hover:border-emerald-500/50"
                      >
                        <AttachMoneyOutlinedIcon sx={{ fontSize: 18 }} />
                        Config Fee
                      </button>
                    </div>
                    <button
                      onClick={() => setDrawerTeacherId(teacher.id)}
                      className="w-full px-4 py-2 bg-gradient-glass hover:bg-orange-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-orange-500/20 hover:border-orange-500/50"
                    >
                      <StarOutlineRounded sx={{ fontSize: 18 }} />
                      Reviews & Recommendations
                    </button>
                    <button
                      onClick={() => copyProfileUrl(teacher.slug)}
                      className="w-full px-4 py-2 bg-gradient-glass hover:bg-blue-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-blue-500/20 hover:border-blue-500/50"
                    >
                      {copiedId === teacher.id ? (
                        <>
                          <CheckIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                          <span className="text-green-400">Copied!</span>
                        </>
                      ) : (
                        <>
                          <LinkIcon sx={{ fontSize: 18 }} />
                          Copy Profile URL
                        </>
                      )}
                    </button>
                    <a
                      href={`/teacher/${teacher.slug}`}
                      className="w-full px-4 py-2 bg-gradient-glass hover:bg-green-500/20 text-light rounded-lg text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 border border-green-500/20 hover:border-green-500/50"
                    >
                      <PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />
                      View Profile
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination — server-side */}
          {meta && meta.last_page > 1 && (
            <div className="flex justify-center mt-8">
              <Pagination
                count={meta.last_page}
                page={currentPage}
                onChange={handlePageChange}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#FFFFFF',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(229, 64, 4, 0.1)',
                      borderColor: '#E54004',
                    },
                  },
                  '& .Mui-selected': {
                    background: 'linear-gradient(90deg, #E54004 0%, #F6911D 100%) !important',
                    color: '#FFFFFF',
                    fontWeight: 'bold',
                    '&:hover': { opacity: 0.9 },
                  },
                }}
                variant="outlined"
                shape="rounded"
                size="large"
              />
            </div>
          )}
        </>
      ) : (
        <div className="bg-gradient-glass backdrop-blur-sm p-12 rounded-lg border border-white/10 text-center">
          <p className="text-gray-400 text-lg">No teachers found matching your criteria.</p>
          {hasActiveFilters && (
            <Button
              onClick={clearFilters}
              sx={{
                mt: 2,
                color: '#F6911D',
                textTransform: 'none',
                '&:hover': { background: 'rgba(246, 145, 29, 0.1)' },
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Stats Footer */}
      {meta && (
        <div className="mt-8 bg-gradient-glass backdrop-blur-sm p-4 rounded-lg border border-white/10">
          <div className="flex items-center justify-between text-gray-300 flex-wrap gap-4">
            <span>
              Total Teachers: <span className="text-light font-bold">{meta.total}</span>
            </span>
            <span>
              Active:{' '}
              <span className="text-green-400 font-bold">
                {teachers.filter((t) => t.status).length}
              </span>
            </span>
            <span>
              Inactive:{' '}
              <span className="text-gray-400 font-bold">
                {teachers.filter((t) => !t.status).length}
              </span>
            </span>
            {hasActiveFilters && (
              <span>
                Filtered:{' '}
                <span className="text-orange-400 font-bold">{teachers.length}</span>
              </span>
            )}
          </div>
        </div>
      )}

      <ReviewsRecsDrawer
        open={!!drawerTeacherId}
        onClose={() => setDrawerTeacherId(null)}
        teacher={drawerTeacher}
        onUpdate={refetch}
      />

      <TeacherFeeModal
        open={!!feeModalTeacher}
        teacher={feeModalTeacher}
        onClose={() => setFeeModalTeacher(null)}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: 'Teacher fee saved successfully.',
            severity: 'success',
          });
        }}
      />

      {/* Snackbar */}
      <AppSnackbar
        open={snackbar.open}
        autoHideDuration={3000}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title="Reject Teacher"
        message="Are you sure you want to delete this teacher? This will permanently delete their profile and cannot be undone."
        confirmText="Delete"
        onClose={() => setConfirmDialog({ open: false, teacherId: null })}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
